import { Chip, TextField as MuiTextField, TextFieldProps } from '@mui/material';
import React, { ChangeEventHandler, KeyboardEventHandler, useMemo, useState } from 'react';
import { BpError } from '../Icons';

export type ControllerChipInputProps = Omit<
  TextFieldProps,
  'select' | 'value' | 'onBlur' | 'onChange' | 'onKeyDown'
> & {
  chips?: string[];
  onChipsChange?: (chips: string[]) => void;
  errorText?: string;
  readOnlyDisabled?: boolean;
  readOnly?: boolean;
};

export const ControllerChipInput = function (props: ControllerChipInputProps) {
  const {
    chips,
    onChipsChange,
    readOnly,
    readOnlyDisabled,
    errorText,
    helperText,
    inputProps,
    InputProps,
    InputLabelProps,
    placeholder,
    ...textFieldProps
  } = props;

  const [value, setValue] = useState('');
  const [selectedChip, setSelectedChip] = useState<string | null>(null);

  const chipSet = useMemo(() => {
    return new Set(chips);
  }, [chips]);

  const handleAddChip = () => {
    const inputValues = value
      ? value.split(',').map((val) => {
          return val.trim();
        })
      : [];

    const newChipSet = new Set([...chipSet, ...inputValues]);
    onChipsChange && onChipsChange([...newChipSet]);
    setValue('');
  };

  const handleDeleteChip = (chip: string) => () => {
    chipSet.delete(chip);
    onChipsChange && onChipsChange([...chipSet]);
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (evt) => {
    if (evt.key === 'Enter') {
      handleAddChip();
      setSelectedChip(null);
    }
    if (evt.key === 'Backspace') {
      if (!value && chipSet.size && selectedChip) {
        chipSet.delete(selectedChip);
        setSelectedChip(null);
      } else if (!value && chipSet.size) {
        const lastChip = [...chipSet]?.pop();
        setSelectedChip(lastChip || null);
      }
    }
  };

  const handleBlur = () => {
    handleAddChip();
  };

  const handleChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (evt) => {
    const { value } = evt.target;
    setSelectedChip(null);
    setValue(value);
  };

  return (
    <MuiTextField
      helperText={props.error && errorText ? errorText : helperText}
      InputLabelProps={{
        className: readOnlyDisabled ? 'Mui-disabled' : undefined,
        ...InputLabelProps,
      }}
      inputProps={{
        // classes added here are merged into existing MUI classes
        className: readOnlyDisabled ? 'Mui-disabled' : undefined,
        ...inputProps,
      }}
      InputProps={{
        readOnly: readOnly || readOnlyDisabled,
        className: readOnlyDisabled ? 'Mui-disabled' : undefined,
        tabIndex: readOnlyDisabled ? -1 : undefined,
        startAdornment: chips?.length
          ? [...chipSet].map((item) => (
              <Chip
                key={item}
                tabIndex={-1}
                color={selectedChip === item ? 'info' : undefined}
                label={item}
                disabled={readOnly || readOnlyDisabled || textFieldProps.disabled}
                onDelete={handleDeleteChip(item)}
                sx={{ margin: '4px 0' }}
              />
            ))
          : null,
        endAdornment: props.error && <BpError color='error' />,
        ...InputProps,
      }}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      value={value}
      placeholder={!chips?.length ? placeholder : undefined}
      sx={{
        '& .MuiInputBase-input': { width: 'auto', flex: '10 1 5px' },
        '& .MuiInputBase-adornedStart': { flexWrap: 'wrap' },
      }}
      {...textFieldProps}
    />
  );
};

ControllerChipInput.displayName = 'ControllerChipInput';
